import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Settings, X, Shield } from "lucide-react";
import {
  getConsent,
  setConsent,
  isConsentDecided,
} from "@/lib/cookieConsent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analiticas: false, marketing: false });

  useEffect(() => {
    // Mostrar banner si no hay decisión previa o si la versión cambió
    if (!isConsentDecided()) {
      // pequeño delay para no chocar con el hero
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // Permitir abrir desde el footer
  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setPrefs({
          analiticas: !!existing.analiticas,
          marketing: !!existing.marketing,
        });
      }
      setExpanded(true);
      setVisible(true);
    };
    window.__openCookiePreferences = open;
    return () => {
      delete window.__openCookiePreferences;
    };
  }, []);

  const acceptAll = useCallback(() => {
    setConsent({ analiticas: true, marketing: false }, "banner_accept_all");
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    setConsent({ analiticas: false, marketing: false }, "banner_reject_all");
    setVisible(false);
  }, []);

  const saveCustom = useCallback(() => {
    setConsent(
      { analiticas: !!prefs.analiticas, marketing: !!prefs.marketing },
      "banner_custom",
    );
    setVisible(false);
  }, [prefs]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 240 }}
          className="fixed bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 z-[80] flex justify-center pointer-events-none"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
        >
          <div className="pointer-events-auto w-full max-w-3xl rounded-2xl bg-ink-950 text-slate-200 shadow-2xl border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="flex items-start gap-3 px-5 pt-5 pb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 text-blue-300 grid place-items-center shrink-0">
                <Cookie className="w-4.5 h-4.5" strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  id="cookie-consent-title"
                  className="text-sm font-semibold text-white flex items-center gap-2"
                >
                  Tu privacidad importa
                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-blue-300/80">
                    <Shield className="w-3 h-3" /> Ley 21.719
                  </span>
                </h2>
                <p className="mt-1 text-xs text-slate-400 leading-relaxed">
                  Usamos cookies y herramientas de análisis. Las{" "}
                  <strong className="text-slate-200">necesarias</strong> siempre están activas
                  (sesión, seguridad). Las <strong className="text-slate-200">analíticas</strong>
                  {" "}(PostHog · Google Analytics, datos a EE.UU.) solo se activan si las aceptás.{" "}
                  <Link
                    to="/politica-cookies"
                    className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                  >
                    Política de cookies
                  </Link>
                  {" · "}
                  <Link
                    to="/politica-privacidad"
                    className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                  >
                    Privacidad
                  </Link>
                </p>
              </div>
              <button
                type="button"
                onClick={rejectAll}
                aria-label="Cerrar y rechazar opcionales"
                className="text-slate-500 hover:text-slate-300 p-1 rounded-md hover:bg-white/5 transition-colors"
                data-testid="cookie-consent-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Detalles expandibles */}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden border-t border-white/10"
                >
                  <ul className="px-5 py-4 space-y-3 text-xs">
                    <li className="flex items-start gap-3">
                      <div className="mt-0.5 w-9 h-5 rounded-full bg-blue-500/30 grid place-items-end px-0.5 shrink-0" aria-hidden="true">
                        <div className="w-4 h-4 rounded-full bg-blue-300" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-white">Necesarias</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Siempre activas</span>
                        </div>
                        <p className="mt-0.5 text-slate-400">Sesión, autenticación, preferencias UI. No persisten datos personales identificables más allá de la sesión.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => setPrefs((p) => ({ ...p, analiticas: !p.analiticas }))}
                        role="switch"
                        aria-checked={prefs.analiticas}
                        aria-label="Cookies analíticas"
                        data-testid="cookie-toggle-analiticas"
                        className={`mt-0.5 w-9 h-5 rounded-full grid items-center px-0.5 shrink-0 transition-colors ${prefs.analiticas ? "bg-emerald-500/80 justify-end" : "bg-slate-700 justify-start"}`}
                      >
                        <div className="w-4 h-4 rounded-full bg-white" />
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-white">Analíticas</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">PostHog · GA4 · EE.UU.</span>
                        </div>
                        <p className="mt-0.5 text-slate-400">Métricas de uso anónimas + session recording (interacciones, sin contenido tecleado en formularios). Datos transferidos a EE.UU. bajo Cláusulas Min. Economía (RAEX 2025-03731). Google Analytics con IP anonimizada.</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3 opacity-60">
                      <div className="mt-0.5 w-9 h-5 rounded-full bg-slate-800 grid place-items-start px-0.5 shrink-0" aria-hidden="true">
                        <div className="w-4 h-4 rounded-full bg-slate-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-slate-300">Marketing</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">No usamos</span>
                        </div>
                        <p className="mt-0.5 text-slate-500">Hoy no usamos remarketing, Meta Pixel, TikTok Pixel ni similares. Si en el futuro los agregamos, te pediremos consentimiento explícito antes.</p>
                      </div>
                    </li>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Botones */}
            <div className="px-5 py-4 border-t border-white/10 bg-white/[0.02] flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="text-xs font-medium text-slate-300 hover:text-white inline-flex items-center gap-1.5 self-start"
                data-testid="cookie-toggle-expand"
              >
                <Settings className="w-3.5 h-3.5" />
                {expanded ? "Ocultar detalles" : "Personalizar"}
              </button>
              <div className="flex flex-col sm:flex-row gap-2">
                {expanded ? (
                  <button
                    type="button"
                    onClick={saveCustom}
                    data-testid="cookie-save-custom"
                    className="h-9 px-4 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold transition-colors"
                  >
                    Guardar elección
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={rejectAll}
                      data-testid="cookie-reject-all"
                      className="h-9 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 text-xs font-semibold transition-colors"
                    >
                      Solo necesarias
                    </button>
                    <button
                      type="button"
                      onClick={acceptAll}
                      data-testid="cookie-accept-all"
                      className="h-9 px-4 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-xs font-semibold transition-colors"
                    >
                      Aceptar todo
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
